import React from 'react';

import { Button } from '@ui';

export interface HeadingProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  className?: string;
  eyebrow?: string;
  title?: string;
  /**
   * String or number → one `<p>`.
   * Array → `<div class="heading-block-text">`:
   * - If **every** item is `string | number`, each item gets its own `<p>` (multiple short paragraphs).
   * - If **any** item is a React element, the whole list is rendered inline inside one wrapper (no per-string `<p>`), so RSC → client serialization (e.g. `Fragment` collapsing to a sibling array) does not split text around `<b>` / links into separate paragraphs.
   * Other nodes → wrapped in `<div class="heading-block-text">` (arbitrary markup).
   */
  description?: React.ReactNode | readonly (string | number | React.ReactNode)[];
  descriptionClassName?: string;
  subtitle?: React.ReactNode;
  headingHint?: React.ReactNode;
  headingLinkText?: string;
  headingLinkHref?: string;
  headingLinkTarget?: '_blank' | '_self' | '_parent' | '_top';
  headingLinkRel?: 'noopener' | 'noreferrer';
  headingLinkAs?: 'a' | 'button';
  headingLinkClass?: string;
  headingLinkStyle?: React.CSSProperties;
  headingLinkOnClick?: () => void;
  headingLinkDisabled?: boolean;
  headingLinkPosition?: 'start' | 'center' | 'end';
  buttonLinkText?: string;
  buttonLinkHref?: string;
  buttonLinkTarget?: '_blank' | '_self' | '_parent' | '_top';
  buttonLinkRel?: 'noopener' | 'noreferrer';
  buttonLinkAs?: 'a' | 'button';
  buttonLinkClass?: string;
  buttonLinkOnClick?: () => void;
  buttonLinkIcon?: React.ReactNode;
  buttonLinkIconPosition?: 'left' | 'right';
  buttonLinkDisabled?: boolean;
}

/** Alias of `HeadingProps` for cross-project imports and legacy consumers. */
export type HeadingConfigProps = HeadingProps;

const Heading: React.FC<HeadingProps> = ({
  as: Component = 'h2',
  className,
  eyebrow,
  title,
  children,
  subtitle,
  description,
  descriptionClassName,
  headingHint,
  headingLinkText,
  headingLinkHref,
  headingLinkTarget,
  headingLinkRel,
  headingLinkAs = 'a',
  headingLinkClass,
  headingLinkStyle,
  headingLinkOnClick,
  headingLinkDisabled,
  headingLinkPosition = 'start',
  buttonLinkText,
  buttonLinkHref,
  buttonLinkTarget,
  buttonLinkRel,
  buttonLinkAs = 'a',
  buttonLinkClass,
  buttonLinkOnClick,
  buttonLinkIcon,
  buttonLinkIconPosition = 'left',
  buttonLinkDisabled,
  ...rest
}) => {
  const resolvedClass = className?.trim() ? className : undefined;
  const headingBody = title ?? children;

  const linkSuffix =
    headingLinkText &&
    (headingLinkAs === 'a' && headingLinkHref ? (
      <span className={`heading-link align-${headingLinkPosition}`}>
        <Button
          as='a'
          href={headingLinkHref}
          target={headingLinkTarget}
          rel={headingLinkRel}
          label={headingLinkText}
          className={headingLinkClass}
          style={headingLinkStyle}
        />
      </span>
    ) : (
      <span className={`heading-link align-${headingLinkPosition}`}>
        <Button
          as='button'
          label={headingLinkText}
          className={headingLinkClass}
          style={headingLinkStyle}
          onClick={headingLinkOnClick}
          disabled={headingLinkDisabled}
        />
      </span>
    ));

  const isHeadingDescriptionPrimitive = (item: unknown): item is string | number =>
    typeof item === 'string' || typeof item === 'number';

  const descriptionBlock =
    description != null && description !== '' && description !== false ? (
      Array.isArray(description) ? (
        description.length > 0 ? (
          description.every(isHeadingDescriptionPrimitive) ? (
            <div className='heading-block-text'>
              {description.map((item, i) => (
                <p key={i} className={descriptionClassName}>
                  {item}
                </p>
              ))}
              {linkSuffix}
            </div>
          ) : (
            <div
              className={['heading-block-text', descriptionClassName].filter(Boolean).join(' ').trim()}
            >
              {description.map((item, i) => (
                <React.Fragment key={i}>{item}</React.Fragment>
              ))}
              {linkSuffix}
            </div>
          )
        ) : null
      ) : typeof description === 'string' || typeof description === 'number' ? (
        <p className={descriptionClassName}>
          {description}
          {linkSuffix}
        </p>
      ) : (
        <div
          className={['heading-block-text', descriptionClassName].filter(Boolean).join(' ').trim()}
        >
          {description}
          {linkSuffix}
        </div>
      )
    ) : null;

  return (
    <div className='heading-block'>
      {eyebrow ? <span className='eyebrow'>{eyebrow}</span> : null}
      {buttonLinkText ? (
        <div className='row space-between nowrap'>
          <Component className={resolvedClass} {...rest}>
            {headingBody}
          </Component>
          {buttonLinkAs === 'a' && buttonLinkHref ? (
            <span>
              <Button
                as='a'
                href={buttonLinkHref}
                target={buttonLinkTarget}
                rel={buttonLinkRel}
                label={buttonLinkText}
                className={buttonLinkClass}
                icon={buttonLinkIcon}
                iconPosition={buttonLinkIconPosition}
              />
            </span>
          ) : (
            <span>
              <Button
                as='button'
                label={buttonLinkText}
                className={buttonLinkClass}
                onClick={buttonLinkOnClick}
                disabled={buttonLinkDisabled}
                icon={buttonLinkIcon}
                iconPosition={buttonLinkIconPosition}
              />
            </span>
          )}
        </div>
      ) : (
        <Component className={resolvedClass} {...rest}>
          {headingBody}
        </Component>
      )}
      {descriptionBlock}
      {subtitle || headingHint ? <p className='signal-line'>{subtitle || headingHint}</p> : null}
    </div>
  );
};

export default Heading;
